import React from 'react';

import ModelTypeConstants from 'constants/ModelTypeConstants';
import BaseListingRendererWithPlugins from 'nav/expanded/BaseListingRendererWithPlugins';
import {
  ColumnAccessorKey,
  ColumnConfiguration,
  ListingRendererType,
} from 'nav/expanded/ListingRendererConstants';
import {getReviewLifecycleTypeText} from 'notebook/reviews/review-lifecycles/ReviewLifecyclesUtil';
import ReviewStatus from 'notebook/reviews/ReviewStatus';
import {SortKey} from 'search/SearchConstants';
import {getColumnHeaderFromSortKey} from 'search/SortUtil';
import {
  maybeGetStudiesListingColumn,
  StudyableItemListingExtras,
} from 'studies/studyable/StudiesListingColumn';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import {TABLE_ROW_BODY_HEIGHT} from 'ui-library/scrollable/ScrollableTableConstants';
import {BasicEvent} from 'util/Types';

import {WorksheetListingGraphQL} from '../../../../graphql-fragments/worksheets/__generated/WorksheetListingGraphQL';

export interface WorksheetListingExtras extends StudyableItemListingExtras {}
export default class WorksheetListingRenderer extends BaseListingRendererWithPlugins<ListingRendererType.WORKSHEET> {
  listingRendererType = ListingRendererType.WORKSHEET;

  renderIcon(): JSX.Element {
    return <BenchlingIcon icon={ModelTypeConstants.DOCUMENT} className="scrollableTable-cell-icon" />;
  }

  getNonPluginColumnConfigurations(
    items: Array<WorksheetListingGraphQL>,
    {itemStudyAttributes}: WorksheetListingExtras = {}
  ): Array<ColumnConfiguration<WorksheetListingGraphQL>> {
    const maybeStudiesColumn = maybeGetStudiesListingColumn<WorksheetListingGraphQL>(itemStudyAttributes);

    const columns: Array<ColumnConfiguration<WorksheetListingGraphQL>> = [
      {
        column: {
          accessorKey: ColumnAccessorKey.NAME,
          header: 'Name',
          flexGrow: 1,
          isSortable: true,
          sortKey: SortKey.SORT_NAME_KEY,
        },
        render: (item) => item.name,
      },
      {
        column: {
          accessorKey: ColumnAccessorKey.ID,
          header: getColumnHeaderFromSortKey(SortKey.SORT_IDENTIFIER_KEY),
          flexGrow: 1,
          isSortable: true,
          sortKey: SortKey.SORT_IDENTIFIER_KEY,
        },
        render: (item) => item.displayId,
      },
      {
        column: {
          header: 'Review Status',
          accessorKey: ColumnAccessorKey.STATUS,
          flexGrow: 1,
          isCollapsible: true,
          isSortable: false,
        },
        render: (item) => <ReviewStatus review={item.review} />,
      },
      {
        column: {
          header: 'Review Process',
          accessorKey: ColumnAccessorKey.REVIEW_LIFECYCLE,
          flexGrow: 1,
          isCollapsible: true,
          isSortable: false,
        },
        render: (item) => (
          <span className="u-ellipsis-text">{item.review?.reviewLifecycleVersion?.name}</span>
        ),
      },
      {
        column: {
          header: 'Review Process Type',
          accessorKey: ColumnAccessorKey.REVIEW_LIFECYCLE_TYPE,
          flexGrow: 1,
          isCollapsible: true,
          isSortable: false,
        },
        render: (item) => (
          <span className="u-ellipsis-text">
            {getReviewLifecycleTypeText(item.review?.reviewLifecycleVersion?.lifecycleType)}
          </span>
        ),
      },
      // TODO(BNCH-104060): Add Columns for Review Initially Started, Review Last Started, Review Completed
      ...(maybeStudiesColumn ? [maybeStudiesColumn] : []),
    ];

    return columns;
  }

  getNonPluginRowContentHeight(item: WorksheetListingGraphQL): number {
    return TABLE_ROW_BODY_HEIGHT;
  }

  openContextMenu(item: WorksheetListingGraphQL, e: BasicEvent, extras: unknown): void {
    // TODO: implement context menu
  }
}
